<script setup>
import { ref, watch } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import ReachabilityStatus from './ReachabilityStatus.vue'
import { isCurrent } from './navigation.js'

const props = defineProps({
    // Same shape as DesktopMenu
    items: {
        type: Array,
        required: true,
    },
    contact: {
        type: Object,
        required: true,
    },
    reachability: {
        type: Object,
        required: true,
    },
})

const open = ref(false)

// Start with the section you're in unfolded
const expandedIndex = ref(props.items.findIndex(item => item.children?.length && isCurrent(item)))

function toggle (index) {
    expandedIndex.value = expandedIndex.value === index ? -1 : index
}

// Close it when the viewport grows into the desktop menu (lg), e.g. when rotating a tablet
const isDesktop = useMediaQuery('(min-width: 64rem)')

watch(isDesktop, value => {
    if (value) open.value = false
})
</script>

<template>
    <USlideover
        v-model:open="open"
        side="right"
        title="Menu"
        :ui="{ content: 'w-full max-w-sm', body: 'p-0 sm:p-0', footer: 'block' }"
    >
        <button
            type="button"
            class="inline-flex items-center justify-center rounded-full text-white cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
            :aria-label="open ? 'Menu sluiten' : 'Menu openen'"
        >
            <icon name="menu" class="size-6"></icon>
        </button>

        <template #body>
            <nav aria-label="Mobiele navigatie">
                <ul role="list" class="divide-y divide-gray-100">
                    <li v-for="(item, index) in items" :key="item.url ?? item.label">

                        <template v-if="item.children?.length">
                            <button
                                type="button"
                                class="flex w-full items-center justify-between gap-4 px-4 py-4 text-left text-base font-semibold cursor-pointer sm:px-6"
                                :class="expandedIndex === index ? 'text-red-500' : 'text-gray-900'"
                                :aria-expanded="expandedIndex === index"
                                @click="toggle(index)"
                            >
                                {{ item.label }}
                                <icon name="chevronDown" class="size-5 text-gray-400 transition-transform duration-200" :class="{ 'rotate-180': expandedIndex === index }"></icon>
                            </button>

                            <!-- Animates to the content's height without measuring it -->
                            <div
                                class="grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none"
                                :class="expandedIndex === index ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
                            >
                                <div class="overflow-hidden" :inert="expandedIndex !== index">
                                    <ul role="list" class="px-2 pb-2 sm:px-4">
                                        <li v-for="child in item.children" :key="child.url">
                                            <a
                                                :href="child.url"
                                                class="flex items-center gap-3 rounded-lg px-2 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50"
                                                :class="{ 'bg-red-50 text-red-600': isCurrent(child, true) }"
                                                :aria-current="isCurrent(child, true) ? 'page' : undefined"
                                            >
                                                <span class="grid size-9 flex-none place-items-center rounded-lg bg-red-50 text-red-500">
                                                    <icon :name="child.icon" class="size-5"></icon>
                                                </span>
                                                {{ child.label }}
                                            </a>
                                        </li>
                                    </ul>
                                    <a
                                        v-if="item.button"
                                        :href="item.button.url"
                                        class="mx-4 mb-4 flex items-center justify-center gap-1.5 rounded-full border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:border-red-500 hover:text-red-500 sm:mx-6"
                                    >
                                        {{ item.button.label }}
                                        <icon name="ArrowRight" class="size-3.5"></icon>
                                    </a>
                                </div>
                            </div>
                        </template>

                        <a
                            v-else
                            :href="item.url"
                            class="flex items-center justify-between px-4 py-4 text-base font-semibold sm:px-6"
                            :class="isCurrent(item) ? 'text-red-500' : 'text-gray-900'"
                            :aria-current="isCurrent(item, true) ? 'page' : undefined"
                        >{{ item.label }}</a>

                    </li>
                </ul>
            </nav>
        </template>

        <template #footer>
            <a
                :href="`tel:${contact.phoneNumber}`"
                class="flex items-center justify-center gap-2 rounded-full bg-red-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-600"
            >
                <icon name="phone" class="size-5"></icon>
                Bel {{ contact.phoneNumber }}
            </a>
            <div class="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-500">
                <ReachabilityStatus :status="reachability" />
                <a :href="`mailto:${contact.email}`" class="underline hover:text-gray-900">{{ contact.email }}</a>
            </div>
        </template>
    </USlideover>
</template>
